import React, { useCallback, useEffect, useState } from 'react';
import type { ComponentProps, PropsWithChildren } from 'react';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { Waypoint } from 'src/app/shared/components/scroll';

import type { WaypointCallbackArgs } from './shared/types';

type Props = {
	fallback?: React.ReactNode;
	topOffset?: ComponentProps<typeof Waypoint>['topOffset'];
	/**
	 * triggers an immediate load of the contents when the user is a bot,
	 * so that the content can be indexed by indexers
	 */
	immediateForBots?: boolean;
};

export const ScrollableComponent: React.FC<PropsWithChildren<Props>> = ({
	children,
	fallback,
	topOffset,
	immediateForBots,
}) => {
	const {
		userAgent: { isBot },
	} = useUtilityContext();
	const [shouldDisplay, setShouldDisplay] = useState(false);

	const onPositionChange = useCallback(
		({ currentPosition }: WaypointCallbackArgs) => {
			setShouldDisplay((alreadyDisplayed) => alreadyDisplayed || currentPosition !== 'below');
		},
		[setShouldDisplay],
	);

	useEffect(() => {
		if (isBot() && immediateForBots) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setShouldDisplay(true);
		}
	}, [immediateForBots, isBot]);

	return (
		<>
			<Waypoint onPositionChange={onPositionChange} topOffset={topOffset} />
			{shouldDisplay ? children : fallback || null}
		</>
	);
};
